import { useCallback, useEffect, useState } from "react";
import {
  BASE_URL,
  checkIfTokenHasExpired,
  checkUserIfExistAndGetToken,
  fetchLoyaltyMemberWithToken,
  getHelper,
  getLocalStorageWithTimer,
  persistLoyaltyToken,
} from "../general/helper";
import { concatActivityIconAndTagsOverrides } from "../general/activitiesTags";

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [tags, setTags] = useState([]);
  const [general, setGeneral] = useState();
  const [daysToShowOnMobile, setDaysToShowOnMobile] = useState(7);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const params = new URLSearchParams(window.location.search);
  const lid = params.get("lid");
  const type = params.get("type");
  const lang = params.get("lang");
  const mobile2FA = getLocalStorageWithTimer(`verifiedMobile-${lid}`);
  const token2FA = localStorage.getItem(`token-${lid}`);

  const fetchProperties = useCallback(async () => {
    try {
      const confGeneral = await getHelper(
        `${BASE_URL}/properties?category=general`,
        { authorization: `lid ${lid}` },
      );
      setGeneral(confGeneral[0])
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  }, [lid]);

  const fetchTags = useCallback(async () => {
    try {
      const configuration = await getHelper(
        `${BASE_URL}/activitiesSchedule/guest/configuration`,
        { authorization: `lid ${lid}` },
      );
      const allTags = await getHelper(
        `${BASE_URL}/activitiesSchedule/guest/tags`,
        { authorization: `lid ${lid}` },
      );
      const tagsWithOverrides = concatActivityIconAndTagsOverrides(
        allTags,
          configuration?.tagsOverrides,
          lang,
      );
      setDaysToShowOnMobile(configuration.daysToShowOnMobile);
      setTags(tagsWithOverrides);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  }, [lid, lang]);

  const fetchUser = useCallback(async () => {
    try {
      const info = await getHelper(`${BASE_URL}/launcher/info`, {
        authorization: `token ${type}`,
      });
      const isTestUser =
        info?.user?.reservationNumber === info?.user?.firstName;
      setUser({ ...info.user, isTestUser });
      setLoggedIn(true);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  }, [type]);

  const loginWith2FA = useCallback(
    async (phone = "") => {
      try {
        // console.log("Starting loginWith2FA");
        let token;

        if (mobile2FA || phone) {
          const mobile = mobile2FA || phone;
          // console.log("mobile2FA found:", mobile);
          const tokenExist = await checkUserIfExistAndGetToken({
            mobile: mobile,
            accommodationId: lid,
          });
          // console.log("checkUserIfExistAndGetToken result:", tokenExist);
          if (!tokenExist) {
            // console.warn("Token not found for mobile2FA");
            return false;
          }
          token = persistLoyaltyToken(lid, tokenExist);
          if (!token) {
            // console.warn("Failed to persist loyalty token after login");
            return false;
          }
            // console.log("Token persisted in localStorage:", token);
        } else if (token2FA) {
          // console.log("token2FA found:", token2FA);
          const tokenIsExpired = await checkIfTokenHasExpired({
            accommodationId: lid,
            token: token2FA,
          });
          // console.log("checkIfTokenHasExpired result:", tokenIsExpired);

          if (tokenIsExpired.payload) {
            // console.warn("Token is expired");
            return false;
          }
          token = persistLoyaltyToken(lid, token2FA);
          if (!token) {
            // console.warn("Failed to persist loyalty token from storage");
            return false;
          }
        } else {
          // console.warn("No mobile2FA or token2FA found");
          return false;
        }

        // console.log("Using token:", token);
        const data = await fetchLoyaltyMemberWithToken({
          token,
          accommodationId: lid,
        });
        // console.log("fetchLoyaltyMemberWithToken result:", data);

        if (!data) {
          // console.warn("No data returned from fetchLoyaltyMemberWithToken");
          return false;
        }

        setUser(data);
        setLoggedIn(true);
        // console.log("User logged in:", data);
        return token;
      } catch (error) {
        // console.error("Login with 2FA error:", error);
        return false;
      }
    },
    [lid, mobile2FA, token2FA],
  );

  useEffect(() => {
    if (type) {
      Promise.all([fetchUser(), fetchTags(), fetchProperties()]).finally(() => setLoading(false));
    } else {
      Promise.all([loginWith2FA(), fetchTags(), fetchProperties()]).finally(() =>
        setLoading(false),
      );
    }
  }, [lid, type, fetchUser, fetchTags, fetchProperties, loginWith2FA]);

  useEffect(() => {
    if (type) {
      if (!loggedIn) {
        fetchUser().finally(() => setLoading(false));
      }
    } else {
      if ((mobile2FA || token2FA) && !loggedIn) {
        loginWith2FA().finally(() => setLoading(false));
      }
    }
  }, [type, loggedIn, mobile2FA, token2FA, fetchUser, loginWith2FA]);

  return {
    user,
    tags,
	general,
    daysToShowOnMobile,
    loggedIn,
    loading,
    lid,
    type,
    loginWith2FA,
    lang,
  };
};

export default useAuth;
